import { Typography } from '@mui/material';
import { Box } from '@mui/system';

import { SEARCH_TEMPLATE_SAVE_TOOLTIP_TEXT } from '@/constants/searchTemplate';
import PlaceholderFieldContentList from '@/pages/SearchTemplatePage/PlaceholderFieldContentList';
import ButtonRow from '@components/ButtonRow';
import ToolTipSpanner from '@/pages/SearchTemplatePage/ToolTipSpanner';
import TemplatePanelDialogs from './TemplatePanelDialogs';
import TemplatePanelHeader from './TemplatePanelHeader';
import { TemplatePanelContentProps } from './templatePanelContentTypes';
import { useTemplatePanelContent } from './useTemplatePanelContent';

export type {
  TemplatePanelContentHandle,
  TemplatePanelContentProps,
} from './templatePanelContentTypes';

export default function TemplatePanelContent({
  ref,
  initialMetadata,
  searchTemplateId,
  initialSearchTree,
  searchTreeIsIncomplete,
  hiddenPlaceholderKeys,
  onAnyChange,
  onTemplateSaveSuccess,
  onPlaceholderClick,
  onPlaceholderBlur,
  selectedPlaceholderKey,
}: TemplatePanelContentProps) {
  const panel = useTemplatePanelContent({
    ref,
    initialMetadata,
    searchTemplateId,
    initialSearchTree,
    searchTreeIsIncomplete,
    hiddenPlaceholderKeys,
    onAnyChange,
    onTemplateSaveSuccess,
    onPlaceholderClick,
    onPlaceholderBlur,
    selectedPlaceholderKey,
  });

  return (
    <>
      <TemplatePanelHeader {...panel} />
      <Box
        sx={{
          borderBottom: "1px solid #0000001f",
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px'
        }}>
        <Typography component="h3" variant="h6">
          <span>
            <strong>Placeholder fields</strong>
          </span>
        </Typography>

        {panel.placeholdersDataIsEmpty && (
          <Typography variant="body1">
            <span>
              <em>Right click in a field in your search tree for placeholder options</em>
            </span>
          </Typography>
        )}
        <PlaceholderFieldContentList
          placeholdersData={panel.placeholdersData}
          onPositionsChange={panel.handlePositionsChange}
          onPlaceholderChange={panel.handlePlaceholderChange}
          hiddenPlaceholderKeys={hiddenPlaceholderKeys}
          ariaTitlesByKey={panel.ariaTitlesByKey}
          onScrollToLeafNodeClick={panel.handleScrollToLeafNode}
          onPlaceholderClick={onPlaceholderClick}
          onPlaceholderBlur={onPlaceholderBlur}
          selectedPlaceholderKey={selectedPlaceholderKey}
        />
      </Box>
      <ButtonRow>
        <ToolTipSpanner
          title={SEARCH_TEMPLATE_SAVE_TOOLTIP_TEXT}
          disabled={panel.previewDisabled}
          buttonVariant="outlined"
          dataTestId="templateBuilderPanel-preview"
          buttonLabel="Preview"
          onClick={panel.handlePreview}
        />
        <ToolTipSpanner
          title={SEARCH_TEMPLATE_SAVE_TOOLTIP_TEXT}
          disabled={panel.saveDisabled}
          buttonVariant="contained"
          dataTestId="templateBuilderPanel-save"
          buttonLabel="Save"
          onClick={panel.handleSaveClick}
        />
      </ButtonRow>
      <TemplatePanelDialogs
        {...panel}
        searchTemplateId={searchTemplateId}
        initialMetadata={initialMetadata}
        onTemplateSaveSuccess={onTemplateSaveSuccess}
      />
    </>
  );
}
